import { useEffect, useState } from 'react';
import { Grid } from '@material-ui/core';
import { Option, Tabs, TextBlock, Toolbar, ToolbarButtonGroup } from 'neon-design-system';
import { PageHeader } from '../../../../components/PageHeader/PageHeader';
import { ToolbarRequestTypeButtonGroup } from '../../../../components/ToolbarRequestTypeButtonGroup/ToolbarRequestTypeButtonGroup';
import {
    CASH_MOVEMENT_ACTIVITY_TYPE_OPTIONS,
    CASH_MOVEMENT_REQUEST_STATUS_OPTIONS,
    CashMovementActivityTypeFilter,
    CashMovementStatusType,
    RequestType
} from '../../../../constants/request';
import { Account, TabValue } from '../../../../types/request';
import { ToolbarDatePicker } from '../../../../components/ToolbarDatePicker/ToolbarDatePicker';
import { Range } from 'react-date-range';
import { useFilters } from '../../../../providers/FiltersProvider';
import {
    CashMovementRequestsGrid,
    pageName
} from '../../components/CashMovementRequestsGrid/CashMovementRequestsGrid';
import { ToolbarMenu } from '../../../../components/ToolbarMenu/ToolbarMenu';
import { useHistory } from 'react-router-dom';
import { baseUrls } from '../../../../constants/urls';
import { ToolbarAccountSelect } from '../../../../components/ToolbarAccountSelect/ToolbarAccountSelect';
import { useUpBreakpoints } from '../../../../providers/BreakpointsProvider';
import { useCashMovementRequestsQuery } from '../../hooks/useCashMovementRequestsQuery';
import { Loader } from '../../../../components/Loader/Loader';
import { ServerErrorView } from '../../../../components/ServerErrorView/ServerErrorView';
import { toOption } from '../../../../utils/select';
import PaginationGridWrapper from '../../../../components/PaginationGridWrapper/PaginationGridWrapper';
import { debounceGetAccounts, useAccountsQuery } from '../../../../hooks/useAccountsQuery';
import { MobileCashMovementRequests } from './MobileCashMovementRequests';
import { usePermissions } from '../../../../providers/PermissionsProvider';
export const activeFilterName = 'cashMovementRequests';
export const CashMovementRequests = () => {
    const { bulkCashMovementRequestsEnabled } = usePermissions();
    const { filters, setFilters } = useFilters();
    const { data: accountsData, isFetching: accountsFetching } = useAccountsQuery();
    const accounts = accountsData || [];
    const { cashMovementRequests } = filters;
    const { md } = useUpBreakpoints();
    const isMobile = !md;
    const [requestType, setRequestType] = useState<RequestType>(cashMovementRequests.requestType);
    const [initLoading, setInitLoading] = useState<boolean>(true);
    const [requestStatus, setRequestStatus] = useState<CashMovementStatusType>(
        cashMovementRequests.requestStatus as any
    ); // Todo: fix any
    const [activityType, setActivityType] = useState<CashMovementActivityTypeFilter>(
        cashMovementRequests.activityType
    );
    const [tab, setTab] = useState<TabValue>(cashMovementRequests.tab);
    const defaultRanges = cashMovementRequests.ranges.map((range) => ({
        ...range,
        startDate: new Date(range.startDate!),
        endDate: new Date(range.endDate!)
    }));
    const [ranges, setRanges] = useState<Range[]>(defaultRanges);
    const [selectedAccounts, setSelectedAccounts] = useState<Option[] | null>(
        cashMovementRequests.selectedAccounts
    );
    const { data, isFetching, isLoading, isError } = useCashMovementRequestsQuery({
        activeFilterName: initLoading && !isMobile ? undefined : activeFilterName,
        pageName
    });
    const cashMovements = data?.data || [];
    const options = accounts.map((account: Account) => toOption(account.id));
    const history = useHistory();

    useEffect(() => {
        setFilters({
            ...filters,
            cashMovementRequests: {
                requestType,
                requestStatus,
                tab,
                ranges,
                selectedAccounts,
                activityType
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityType, ranges, requestStatus, requestType, selectedAccounts, setFilters, tab]);

    useEffect(() => {
        if (!isFetching && !isError) {
            setInitLoading(false);
        }
    }, [isFetching, isError]);

    if (isFetching && accountsFetching && initLoading) {
        return <Loader />;
    }

    if (isError) {
        return <ServerErrorView />;
    }

    return isMobile ? (
        <MobileCashMovementRequests
            isFetching={isFetching}
            cashMovements={cashMovements}
            options={options}
            selectedAccounts={selectedAccounts}
            setSelectedAccounts={setSelectedAccounts}
            debounceGetAccounts={debounceGetAccounts}
            receiptsAvailable={data?.receiptsAvailable !== false}
        />
    ) : (
        <Grid container spacing={4}>
            <Grid item xs={12}>
                <PageHeader
                    title="Cash Movement Requests"
                    buttonLabel="Request withdrawal"
                    onClick={() =>
                        history.push(
                            baseUrls.cashMovementNewRequest.replace(':paymentType', 'withdrawal')
                        )
                    }
                    secondaryButtonLabel={
                        bulkCashMovementRequestsEnabled ? 'Bulk Request' : undefined
                    }
                    secondaryButtonOnClick={() => history.push(baseUrls.cashMovementBulkRequest)}
                    belowHeading={
                        <Tabs
                            tabs={[
                                { title: 'Latest activity', value: 'latest' },
                                { title: 'All requests', value: 'all' }
                            ]}
                            value={tab}
                            onChange={(v) => setTab(v as TabValue)}
                        />
                    }
                    leftToolbar={
                        tab === 'all' ? (
                            <Toolbar>
                                <ToolbarRequestTypeButtonGroup
                                    type={requestType}
                                    setRequestType={setRequestType}
                                />
                                <ToolbarButtonGroup>
                                    <ToolbarMenu
                                        selectedOption={activityType}
                                        options={CASH_MOVEMENT_ACTIVITY_TYPE_OPTIONS}
                                        onChange={(value) =>
                                            setActivityType(value as CashMovementActivityTypeFilter)
                                        }
                                    />
                                    <ToolbarMenu
                                        selectedOption={requestStatus}
                                        options={CASH_MOVEMENT_REQUEST_STATUS_OPTIONS}
                                        onChange={(value) =>
                                            setRequestStatus(value as CashMovementStatusType)
                                        }
                                    />
                                    <ToolbarDatePicker
                                        selectedRanges={ranges}
                                        onChange={(item) => setRanges([item.selection])}
                                    />
                                </ToolbarButtonGroup>
                            </Toolbar>
                        ) : (
                            <TextBlock styleName="footnote">
                                Showing the most recent requests
                            </TextBlock>
                        )
                    }
                    rightToolbar={
                        tab === 'all' && (
                            <ToolbarAccountSelect
                                loadOptions={debounceGetAccounts}
                                options={options}
                                selected={selectedAccounts}
                                onChange={setSelectedAccounts}
                            />
                        )
                    }
                />
            </Grid>
            {data?.receiptsAvailable === false && (
                <Grid item xs={12} role="status">
                    <TextBlock shade={400}>
                        Receipt allocations are temporarily unavailable. Payment activity may still be
                        shown.
                    </TextBlock>
                </Grid>
            )}
            <Grid item xs={12}>
                {isLoading ? (
                    <Loader fixedHeight />
                ) : (
                    <PaginationGridWrapper
                        hidePagination={tab === 'latest'}
                        rowData={tab === 'latest' ? cashMovements.slice(0, 15) : cashMovements}
                        GridComponent={CashMovementRequestsGrid}
                        activeFilterName={activeFilterName}
                        noRequestsToDisplayDescription="There are no requests to display. Try changing some filters to update this view or create a new request."
                    />
                )}
            </Grid>
        </Grid>
    );
};

export default CashMovementRequests;
